<!-- Backdrop — only visible in expanded (dialog) mode. Stays mounted so
     OnPush + ngIf timing can't make it skip a render. Clicking it pops
     the user back to drawer mode (does not close). -->
<div
    class="gable-backdrop"
    [class.is-visible]="isOpen && isExpanded"
    (click)="onClose()"
    aria-hidden="true"></div>

<div
    class="gable-drawer"
    [class.is-open]="isOpen"
    [class.is-expanded]="isExpanded"
    [ngStyle]="drawerStyle"
    role="dialog"
    aria-label="Gable AI Assistant">

    <!-- Resize strip down the whole left edge — drawer mode only; expanded
         mode has fixed viewport-relative size so resize doesn't apply. Gated
         on isOpen too: the strip straddles the border at left: -2px, so on a
         closed drawer (parked at translateX(width)) those 2px poke back onto
         the screen — a live drag target for a drawer that isn't there. The
         strip itself is invisible; the pill only shows on hover/drag. -->
    <span
        *ngIf="isOpen && !isExpanded"
        class="gable-resize-handle"
        appGableDrawerResize
        matTooltip="Resize"
        matTooltipPosition="left"
        [matTooltipShowDelay]="400"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize drawer"></span>



    <!-- ============================================================ -->
    <!-- View tabs — Chat + Activity (both live, SD-7969). Kill        -->
    <!-- switch: localStorage 'gable-autopilot-ui' = 'off'.            -->
    <!-- ============================================================ -->
    <!-- Expanded mode has no tab strip (Figma): nav lives in the left rail. -->
    <!-- Standard V2 primary tabs (mat-tab-group[tabPrimary]): header-only —
         it drives activeTab, the panel content renders in the sections below
         (the drawer body is shared with expanded mode), so the tab
         body wrapper is hidden in SCSS. Icons are optional per tab via the
         mat-tab-label template. -->
    <div
        *ngIf="autopilotUi && !isExpanded"
        class="gable-tabs">
        <mat-tab-group
            tabPrimary
            [fitInkBarToContent]="true"
            [animationDuration]="0"
            [selectedIndex]="compactTabIndex"
            (selectedIndexChange)="onCompactTabIndexChange($event)"
            aria-label="Gable views">
            <mat-tab>
                <ng-template mat-tab-label>
                    <i class="ph-bold ph-chat-circle icon-16" aria-hidden="true"></i>
                    <span>Chat</span>
                </ng-template>
            </mat-tab>
            <!-- No Autopilot settings tab (CEO decision 2026-07-11): Autopilot is
                 suggestions-only — Chat + Activity are the whole surface. -->
            <mat-tab>
                <ng-template mat-tab-label>
                    <i class="ph-bold ph-clock-counter-clockwise icon-16" aria-hidden="true"></i>
                    <span>Activity</span>
                    <span class="status-tab-count" *ngIf="pendingProposals.length > 0">{{ pendingProposals.length }}</span>
                </ng-template>
            </mat-tab>
            <!-- Instructions — visible to every Gable user (SD-9302), not super-gated. -->
            <mat-tab>
                <ng-template mat-tab-label>
                    <i class="ph-bold ph-article icon-16" aria-hidden="true"></i>
                    <span>Instructions</span>
                </ng-template>
            </mat-tab>
            <!-- Skills — super-only management surface (SD-9197). -->
            <mat-tab *ngIf="isSuperUser">
                <ng-template mat-tab-label>
                    <i class="ph-bold ph-scroll icon-16" aria-hidden="true"></i>
                    <span>Skills</span>
                </ng-template>
            </mat-tab>
        </mat-tab-group>
    </div>

    <!-- ============================================================ -->
    <!-- Drawer-mode navigation menu (off the title caret)            -->
    <!-- ============================================================ -->
    <!-- Mirrors the expanded rail: same nav rows, same recents, same archived
         entry. The drawer has no room for a permanent rail, so the title names
         where you are and the caret is how you leave. This replaced the tab
         strip that used to sit under the header — the two were redundant, and
         the tabs couldn't reach recents or archived at all.
         Gates are copied from the rail verbatim, NOT from the old tab strip:
         Activity on `autopilotUi`, Preferences ungated (SD-9302), Skills
         super-only (SD-9197). -->
    <mat-menu #historyMenu="matMenu" xPosition="before" class="gable-menu-panel gable-history-menu">
        <button
            mat-menu-item
            class="gable-menu-nav"
            [class.is-current-view]="isNewChatSelected"
            [attr.aria-current]="isNewChatSelected ? 'true' : null"
            (click)="onNewChat()">
            <i class="ph-bold ph-plus icon-16 icon-text-alt" aria-hidden="true"></i>
            <span>New chat</span>
            <i class="ph-bold ph-check gable-menu-check icon-16" *ngIf="isNewChatSelected" aria-hidden="true"></i>
        </button>

        <button
            mat-menu-item
            class="gable-menu-nav"
            *ngIf="autopilotUi"
            [class.is-current-view]="activeTab === 'activity'"
            [attr.aria-current]="activeTab === 'activity' ? 'true' : null"
            (click)="onSelectTab('activity')">
            <i class="ph-bold ph-clock-counter-clockwise icon-16 icon-text-alt" aria-hidden="true"></i>
            <span>Activity</span>
            <span class="gable-menu-count" *ngIf="pendingProposals.length > 0">{{ pendingProposals.length }}</span>
            <i class="ph-bold ph-check gable-menu-check icon-16" *ngIf="activeTab === 'activity'" aria-hidden="true"></i>
        </button>

        <!-- Instructions — visible to every Gable user (SD-9302), not super-gated. -->
        <button
            mat-menu-item
            class="gable-menu-nav"
            [class.is-current-view]="activeTab === 'prefs'"
            [attr.aria-current]="activeTab === 'prefs' ? 'true' : null"
            (click)="onSelectTab('prefs')">
            <i class="ph-bold ph-article icon-16 icon-text-alt" aria-hidden="true"></i>
            <span>Instructions</span>
            <i class="ph-bold ph-check gable-menu-check icon-16" *ngIf="activeTab === 'prefs'" aria-hidden="true"></i>
        </button>

        <!-- Skills — super-only management surface (SD-9197). -->
        <button
            mat-menu-item
            class="gable-menu-nav"
            *ngIf="isSuperUser"
            [class.is-current-view]="activeTab === 'skills'"
            [attr.aria-current]="activeTab === 'skills' ? 'true' : null"
            (click)="onSelectTab('skills')">
            <i class="ph-bold ph-scroll icon-16 icon-text-alt" aria-hidden="true"></i>
            <span>Skills</span>
            <i class="ph-bold ph-check gable-menu-check icon-16" *ngIf="activeTab === 'skills'" aria-hidden="true"></i>
        </button>

        <mat-divider></mat-divider>

        <!-- Same word and same treatment as the rail's first bucket — an
             uppercase micro-label with its own icon read as a different kind of
             list than the one the dialog shows. The menu carries one bucket, so
             it names the rail's first one. -->
        <div class="gable-history-menu-label">Recents</div>

        <ng-container *ngIf="recentMenuConversations.length > 0; else noRecent">
            <button
                mat-menu-item
                *ngFor="let c of recentMenuConversations; trackBy: trackByConversationId"
                [class.is-current-chat]="c.id === activeConversationId"
                (click)="onSelectConversation(c)">
                <span class="gable-history-menu-title">{{ conversationLabel(c) }}</span>
            </button>
        </ng-container>
        <ng-template #noRecent>
            <div class="gable-history-menu-empty">No recent chats yet.</div>
        </ng-template>

        <mat-divider></mat-divider>

        <!-- Opens the SAME full Archived panel the expanded rail opens. It used to
             be a select-only submenu, which could neither restore nor rename —
             the two actions a narrow viewport had no other route to. -->
        <button
            mat-menu-item
            class="gable-menu-nav"
            [class.is-current-view]="activeTab === 'archived'"
            [attr.aria-current]="activeTab === 'archived' ? 'true' : null"
            (click)="onSelectTab('archived')">
            <i class="ph-bold ph-archive icon-16 icon-text-alt" aria-hidden="true"></i>
            <span>Archived chats</span>
            <i class="ph-bold ph-check gable-menu-check icon-16" *ngIf="activeTab === 'archived'" aria-hidden="true"></i>
        </button>
    </mat-menu>

    <!-- ============================================================ -->
    <!-- CONTENT ROW — expanded mode puts a history sidebar to the     -->
    <!-- left of the chat column; drawer mode collapses the sidebar.   -->
    <!-- ============================================================ -->
    <div class="gable-content-row">

    <!-- ============================================================ -->
    <!-- SD-6742 — expanded-mode history sidebar (per Figma)          -->
    <!-- ============================================================ -->
    <aside class="gable-sidebar" *ngIf="isExpanded">
        <div class="gable-sidebar-search">
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="gable-sidebar-search-field">
                <i class="ph-bold ph-magnifying-glass icon-muted icon-16" matIconPrefix aria-hidden="true"></i>
                <input
                    #historySearchInput
                    matInput
                    type="text"
                    [attr.aria-label]="'Search chats'"
                    placeholder="Search chats"
                    [ngModel]="historySearch"
                    aria-describedby="gable-find-hint"
                    (ngModelChange)="onHistorySearchChange($event)"
                    (keydown)="onHistorySearchKeydown($event)">
                <span class="sr-only" id="gable-find-hint">
                    Press Enter for the next match, Shift plus Enter for the previous, Escape to clear.
                </span>
                <!-- Chrome-style find controls over the marks in the open
                     thread: counter, prev/next, clear. The counter arrives one
                     frame after the keystroke (the highlight walk is rAF'd), so
                     "0 / 0" is the resting state rather than a hidden block
                     that would flicker in. -->
                <span class="gable-find-controls" matIconSuffix *ngIf="historySearch">
                    <ng-container *ngIf="showFindControls">
                        <span class="gable-find-count" aria-hidden="true">
                            {{ matchCount > 0 ? matchActiveIndex + 1 : 0 }} / {{ matchCount }}
                        </span>
                        <!-- The visible counter is glanceable shorthand; this is the
                             same state in words, for AT. Without it, pressing Enter
                             to cycle matches produces no feedback at all. -->
                        <span class="sr-only" role="status" aria-live="polite">
                            {{ matchCount > 0
                                ? 'Match ' + (matchActiveIndex + 1) + ' of ' + matchCount
                                : 'No matches' }}
                        </span>
                        <button
                            type="button"
                            class="gable-find-btn"
                            [disabled]="matchCount === 0"
                            matTooltip="Previous match (Shift+Enter)"
                            matTooltipClass="gable-dark-tooltip"
                            (click)="onFindPrev()"
                            aria-label="Previous match">
                            <i class="ph-bold ph-caret-up icon-muted icon-12" aria-hidden="true"></i>
                        </button>
                        <button
                            type="button"
                            class="gable-find-btn"
                            [disabled]="matchCount === 0"
                            matTooltip="Next match (Enter)"
                            matTooltipClass="gable-dark-tooltip"
                            (click)="onFindNext()"
                            aria-label="Next match">
                            <i class="ph-bold ph-caret-down icon-muted icon-12" aria-hidden="true"></i>
                        </button>
                        <span class="gable-find-sep" aria-hidden="true"></span>
                    </ng-container>
                    <button
                        type="button"
                        class="gable-find-btn"
                        matTooltip="Clear search (Esc)"
                        matTooltipClass="gable-dark-tooltip"
                        (click)="onClearHistorySearch()"
                        aria-label="Clear search">
                        <i class="ph-bold ph-x icon-muted icon-14" aria-hidden="true"></i>
                    </button>
                </span>
                <!-- Spinner lives in the field so it's visible during a search
                     no matter how long the recent list is (the bottom-of-list
                     spinner can scroll out of view). -->
                <!-- The find controls own the suffix slot once there's a term;
                     the spinner only shows on a term-less background load. -->
                <i
                    *ngIf="conversationsLoading && !historySearch"
                    matIconSuffix
                    class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin"
                    aria-hidden="true"></i>
            </mat-form-field>
        </div>

        <nav class="gable-sidebar-nav">
            <button type="button"
                    class="gable-sidebar-item gable-sidebar-new"
                    [class.is-active]="isNewChatSelected"
                    [attr.aria-current]="isNewChatSelected ? 'true' : null"
                    (click)="onNewChat()">
                <i class="ph-bold ph-plus icon-16 icon-text-alt" aria-hidden="true"></i>
                <span>New chat</span>
            </button>

            <!-- Expanded mode replaces the tab strip: Activity nav lives here (Figma). -->
            <button type="button"
                    class="gable-sidebar-item gable-sidebar-activity"
                    *ngIf="autopilotUi"
                    [class.is-active]="activeTab === 'activity'"
                    [attr.aria-current]="activeTab === 'activity' ? 'true' : null"
                    (click)="onSelectTab('activity')">
                <i class="ph-bold ph-clock-counter-clockwise icon-16 icon-text-alt" aria-hidden="true"></i>
                <span>Activity</span>
                <span class="gable-sidebar-count" *ngIf="pendingProposals.length > 0">{{ pendingProposals.length }}</span>
            </button>

            <!-- Instructions — visible to every Gable user (SD-9302), not super-gated. -->
            <button type="button"
                    class="gable-sidebar-item gable-sidebar-preferences"
                    [class.is-active]="activeTab === 'prefs'"
                    [attr.aria-current]="activeTab === 'prefs' ? 'true' : null"
                    (click)="onSelectTab('prefs')">
                <i class="ph-bold ph-article icon-16 icon-text-alt" aria-hidden="true"></i>
                <span>Instructions</span>
            </button>

            <!-- Skills — super-only management surface (SD-9197). -->
            <button type="button"
                    class="gable-sidebar-item gable-sidebar-skills"
                    *ngIf="isSuperUser"
                    [class.is-active]="activeTab === 'skills'"
                    [attr.aria-current]="activeTab === 'skills' ? 'true' : null"
                    (click)="onSelectTab('skills')">
                <i class="ph-bold ph-scroll icon-16 icon-text-alt" aria-hidden="true"></i>
                <span>Skills</span>
            </button>
        </nav>

        <div class="gable-sidebar-list" (scroll)="onSidebarListScroll($event)">
            <ng-container *ngIf="activeConversations.length > 0; else noRecentSidebar">
                <ng-container *ngIf="recentConversations.length > 0">
                    <div class="gable-sidebar-section-label">Recents</div>
                    <ng-container *ngFor="let c of recentConversations; trackBy: trackByConversationId">
                        <ng-container *ngTemplateOutlet="sidebarChatRow; context: { $implicit: c }"></ng-container>
                    </ng-container>
                </ng-container>
                <ng-container *ngIf="yesterdayConversations.length > 0">
                    <div class="gable-sidebar-section-label">Yesterday</div>
                    <ng-container *ngFor="let c of yesterdayConversations; trackBy: trackByConversationId">
                        <ng-container *ngTemplateOutlet="sidebarChatRow; context: { $implicit: c }"></ng-container>
                    </ng-container>
                </ng-container>
                <ng-container *ngIf="olderConversations.length > 0">
                    <div class="gable-sidebar-section-label">Older</div>
                    <ng-container *ngFor="let c of olderConversations; trackBy: trackByConversationId">
                        <ng-container *ngTemplateOutlet="sidebarChatRow; context: { $implicit: c }"></ng-container>
                    </ng-container>
                </ng-container>
            </ng-container>

            <!-- Inline footer spinner while a page (search reset / load-more) loads. -->
            <div
                class="gable-sidebar-loading"
                *ngIf="conversationsLoading && activeConversations.length > 0"
                aria-busy="true">
                <i class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin" aria-hidden="true"></i>
                <span>Loading…</span>
            </div>

            <ng-template #noRecentSidebar>
                <!-- Suppress the empty copy while the first page is still loading
                     so we don't flash "No recent chats" before results land. -->
                <div class="gable-sidebar-empty" *ngIf="!conversationsLoading">
                    {{ isSearchingHistory ? 'No chats match your search.' : 'No recent chats yet.' }}
                </div>
                <div
                    class="gable-sidebar-loading"
                    *ngIf="conversationsLoading"
                    aria-busy="true">
                    <i class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin" aria-hidden="true"></i>
                    <span>Loading…</span>
                </div>
            </ng-template>
        </div>

        <!-- Shared row markup for a chat-history entry — rendered once per
             time bucket above via ngTemplateOutlet. -->
        <ng-template #sidebarChatRow let-c>
            <div
                class="gable-sidebar-row"
                [class.is-active]="activeTab === 'chat' && c.id === activeConversationId"
                [class.is-editing]="editingConversationId === c.id">
                <input
                    *ngIf="editingConversationId === c.id"
                    class="gable-sidebar-rename-input"
                    type="text"
                    maxlength="255"
                    [(ngModel)]="editingTitle"
                    (keydown.enter)="$event.preventDefault(); commitInlineRename(c)"
                    (keydown.escape)="$event.preventDefault(); cancelInlineRename()"
                    (blur)="commitInlineRename(c)"
                    (click)="$event.stopPropagation()"
                    aria-label="Rename chat">
                <ng-container *ngIf="editingConversationId !== c.id">
                    <button
                        type="button"
                        class="gable-sidebar-item gable-sidebar-chat"
                        [attr.aria-current]="activeTab === 'chat' && c.id === activeConversationId ? 'true' : null"
                        (click)="onSelectConversation(c)">
                        <!-- Title only. The chat's scope reads under the header
                             title of the chat you actually opened (.gable-title-scope)
                             — one line in one place beats a label on every row. -->
                        <span class="gable-sidebar-chat-title" [innerHTML]="conversationLabel(c) | highlight: historySearch"></span>
                    </button>
                    <button
                        type="button"
                        class="gable-sidebar-overflow"
                        [matMenuTriggerFor]="rowMenu"
                        matTooltip="Chat options"
                        matTooltipClass="gable-dark-tooltip"
                        aria-label="Chat options"
                        (click)="$event.stopPropagation()">
                        <i class="ph-bold ph-dots-three icon-muted icon-16" aria-hidden="true"></i>
                    </button>
                    <mat-menu #rowMenu="matMenu" xPosition="before" class="gable-menu-panel">
                        <button mat-menu-item (click)="onRenameConversation(c)">
                            <i class="ph-bold ph-pencil-simple icon-muted icon-16" aria-hidden="true"></i>
                            <span>Rename</span>
                        </button>
                        <button mat-menu-item (click)="onArchiveConversation(c)">
                            <i class="ph-bold ph-archive icon-muted icon-16" aria-hidden="true"></i>
                            <span>Archive</span>
                        </button>
                    </mat-menu>
                </ng-container>
            </div>
        </ng-template>

        <!-- Archived chats pinned to the bottom of the sidebar. Opens the full
             Archived panel in the conversation column (search / filter / sort)
             rather than expanding an inline list — a nested scroller inside the
             rail couldn't hold a paged archive. -->
        <div class="gable-sidebar-archived">
            <button
                type="button"
                class="gable-sidebar-item gable-sidebar-archived-toggle"
                [class.is-active]="activeTab === 'archived'"
                [attr.aria-current]="activeTab === 'archived' ? 'true' : null"
                (click)="onSelectTab('archived')">
                <i class="ph-bold ph-archive icon-16 icon-text-alt" aria-hidden="true"></i>
                <span>Archived chats</span>
            </button>
        </div>
    </aside>

    <div class="gable-main">

        <!-- ============================================================ -->
        <!-- HEADER                                                       -->
        <!-- ============================================================ -->
        <header class="gable-header">
            <div class="gable-title-col">
                <!-- Named explicitly: the info button below is a descendant, and
                     accessible-name computation concatenates descendant names, so
                     without this the heading announces "Instructions About
                     Instructions". The button keeps its own name and stays
                     operable; only the h2's name is pinned to the title. -->
                <h2 class="gable-title" [attr.aria-label]="chatHeaderTitle">
                        <!-- SD-6742: in drawer mode the title names the active view
                             (the open chat, or Activity / Preferences / Skills) and
                             its caret opens the nav menu — the drawer's stand-in for
                             the expanded rail. Expanded mode has the real rail, so
                             the title stays a plain label there. -->
                        <!-- Rename happens where the title already is, in either
                             mode — no dialog, and no hunting for the chat's row. -->
                        <input
                            *ngIf="editingHeaderTitle; else headerTitleDisplay"
                            #headerTitleInput
                            class="gable-title-rename-input"
                            type="text"
                            maxlength="255"
                            [(ngModel)]="headerTitleDraft"
                            (keydown.enter)="$event.preventDefault(); commitHeaderRename()"
                            (keydown.escape)="$event.preventDefault(); cancelHeaderRename()"
                            (blur)="commitHeaderRename()"
                            aria-label="Rename chat">
                        <ng-template #headerTitleDisplay>
                        <ng-container *ngIf="!isExpanded; else plainTitle">
                            <button
                                type="button"
                                class="gable-title-trigger"
                                [matMenuTriggerFor]="historyMenu"
                                (menuOpened)="onHistoryMenuOpened()"
                                [attr.aria-label]="chatHeaderTitle + ' — Gable menu'">
                                <span class="gable-title-text">{{ chatHeaderTitle }}</span>
                                <i class="ph-bold ph-caret-down gable-title-caret icon-muted icon-12" aria-hidden="true"></i>
                            </button>
                        </ng-container>
                        <ng-template #plainTitle>
                            <span class="gable-title-text">{{ chatHeaderTitle }}</span>
                        </ng-template>
                        </ng-template>
                    <!-- The panel's description hangs off the title the whole
                         drawer already shows, so no panel has to spend a header
                         block on it. -->
                    <ng-container *ngIf="activeTabInfo as info">
                        <button
                            type="button"
                            class="gable-title-info"
                            [matTooltip]="info"
                            matTooltipClass="gable-dark-tooltip"
                            [attr.aria-label]="'About ' + chatHeaderTitle">
                            <i class="ph-bold ph-info icon-14" aria-hidden="true"></i>
                        </button>
                    </ng-container>
                </h2>
                <!-- Tenant of the open chat, moved off the sidebar rows. Empty
                     (and unrendered) whenever the chat belongs to the scope the
                     user is already in, so it only speaks up when it matters. -->
                <span *ngIf="headerScopeLabel" class="gable-title-scope">{{ headerScopeLabel }}</span>
                <!-- The "Up to date" / "N suggestions need review" status line used
                     to render here (SD-7969). Parked: it read as chat state rather
                     than as the background review queue it actually counts. The data
                     still flows (`pendingProposals`), the Activity deep-link now
                     lives in the title menu, and the `.gable-status-line` styles are
                     still in the SCSS for whenever it comes back. -->
            </div>
            <!-- No flex-spacer here: .gable-title-col is flex:1 and already pushes
                 the buttons to the right. A spacer (also flex:1) would split the
                 free space with the title, capping it at ~half the header. -->
            <!-- Manage the OPEN chat. Present in every mode, which is the point:
                 rename and archive used to live only in the expanded rail's row
                 kebab, so a viewport under EXPANDED_MIN_VIEWPORT could do
                 neither. Restore lives in the Archived panel, now reachable in
                 the drawer too. -->
            <button
                *ngIf="canManageActiveChat"
                type="button"
                class="gable-header-btn"
                [matMenuTriggerFor]="activeChatMenu"
                matTooltip="Chat options"
                matTooltipClass="gable-dark-tooltip"
                aria-label="Chat options">
                <i class="ph-bold ph-dots-three icon-muted icon-16" aria-hidden="true"></i>
            </button>
            <mat-menu #activeChatMenu="matMenu" xPosition="before" class="gable-menu-panel">
                <button mat-menu-item (click)="onRenameActiveChat()">
                    <i class="ph-bold ph-pencil-simple icon-muted icon-16" aria-hidden="true"></i>
                    <span>Rename chat</span>
                </button>
                <button mat-menu-item (click)="onArchiveActiveChat()">
                    <i class="ph-bold ph-archive icon-muted icon-16" aria-hidden="true"></i>
                    <span>Archive chat</span>
                </button>
            </mat-menu>

            <!-- SD-6742: quick new-chat affordance in the header. -->
            <button
                type="button"
                class="gable-header-btn"
                matTooltip="Start new chat"
                matTooltipClass="gable-dark-tooltip"
                (click)="onNewChat()"
                aria-label="Start new chat">
                <i class="ph-bold ph-plus icon-muted icon-16" aria-hidden="true"></i>
            </button>
            <!-- Hidden while the viewport is too narrow for the dialog: the
                 toggle would set the preference to `expanded` and nothing would
                 visibly happen. -->
            <button
                *ngIf="!isNarrowViewport"
                type="button"
                class="gable-header-btn"
                [matTooltip]="isExpanded ? 'Collapse to drawer' : 'Expand to full screen'"
                matTooltipClass="gable-dark-tooltip"
                (click)="onToggleDock()"
                [attr.aria-label]="isExpanded ? 'Collapse to drawer' : 'Expand to dialog'">
                <i
                    class="ph-bold icon-muted icon-16"
                    [class.ph-arrows-out-simple]="!isExpanded"
                    [class.ph-arrows-in-simple]="isExpanded"
                    aria-hidden="true"></i>
            </button>
            <button
                type="button"
                class="gable-header-btn"
                matTooltip="Close"
                matTooltipClass="gable-dark-tooltip"
                (click)="onClose()"
                aria-label="Close drawer">
                <i class="ph-bold ph-x icon-muted icon-16" aria-hidden="true"></i>
            </button>
        </header>

        <!-- The drawer's tab strip (Chat / Activity / Preferences / Skills)
             used to sit here. It is now the title-caret menu above, which also
             reaches recents and archived — things the tabs could not. -->

    <!-- SD-9208/SD-9209 — persistent scope banner (PMC portfolio or drill-down), above every view. -->
    <div
        class="gable-scope-banner"
        *ngIf="isPmcScoped">
        <i class="ph-bold ph-buildings icon-16" aria-hidden="true"></i>
        <ng-container *ngIf="drillDownOrg; else portfolioScope">
            <span>Scoped to {{ drillDownOrg.name }}</span>
            <flex-spacer></flex-spacer>
            <button type="button" class="gable-scope-banner-btn" (click)="onExitDrillDown()">
                Back to portfolio
            </button>
        </ng-container>
        <ng-template #portfolioScope>
            <span *ngIf="pmcName; else scopeFallback">Portfolio scope — all communities managed by {{ pmcName }}</span>
            <ng-template #scopeFallback><span>Portfolio scope — all communities in your portfolio</span></ng-template>
        </ng-template>
    </div>

    <!-- ============================================================ -->
    <!-- CONVERSATION — the thread scrolls, the composer stays pinned. -->
    <!-- Both wrappers need min-height: 0 or the thread refuses to     -->
    <!-- shrink and pushes the composer out of the dialog.             -->
    <!-- ============================================================ -->
    <div class="gable-conversation">
    <div class="gable-conversation-col">

    <!-- ============================================================ -->
    <!-- BODY (chat)                                                  -->
    <!-- ============================================================ -->
    <section class="gable-body" cdkScrollable *ngIf="activeTab === 'chat'" #conversationScroller (scroll)="onScroll()">

        <!-- LOADING SKELETON — shown while opening a past conversation so the
             switch feels instant and chat-shaped placeholders fill the gap. -->
        <ng-container *ngIf="historyLoading">
            <div class="gable-skeleton" aria-label="Loading conversation" aria-busy="true">
                <div class="gable-skeleton-msg gable-skeleton-msg--user">
                    <span class="gable-skeleton-bubble gable-skeleton-bubble--sm"></span>
                </div>
                <div class="gable-skeleton-msg gable-skeleton-msg--assistant">
                    <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                    <span class="gable-skeleton-line gable-skeleton-line--md"></span>
                    <span class="gable-skeleton-line gable-skeleton-line--sm"></span>
                </div>
                <div class="gable-skeleton-msg gable-skeleton-msg--user">
                    <span class="gable-skeleton-bubble gable-skeleton-bubble--xs"></span>
                </div>
                <div class="gable-skeleton-msg gable-skeleton-msg--assistant">
                    <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                    <span class="gable-skeleton-line gable-skeleton-line--md"></span>
                </div>
            </div>
        </ng-container>

        <!-- EMPTY STATE -->
        <ng-container *ngIf="isEmptyState && !historyLoading">
            <div class="gable-empty-state" [class.is-hero]="autopilotUi">
                <span class="gable-empty-mascot" aria-hidden="true">
                    <i class="ph-bold ph-sparkle" aria-hidden="true"></i>
                </span>

                <!-- Autopilot preview hero — "I'm in your space, ready to help".
                     Capability cards send REAL prompts through the live chat;
                     the status strip deep-links to the (mock) Activity tab. -->
                <ng-container *ngIf="autopilotUi; else classicGreeting">
                    <!-- Figma 2000:31132 — left-aligned greeting; the trust
                         promise is part of the intro paragraph, no separate
                         shield line or blue attention strip. -->
                    <h3 class="gable-greeting gable-hero-greeting">{{ heroGreeting }}</h3>
                    <p class="gable-hero-tagline">
                        I keep an eye on your community around the clock and draft
                        suggested actions across every module. Nothing is sent,
                        charged, or changed until you approve it.
                    </p>

                    <div class="gable-status-strip" *ngIf="showHeroStrip">
                        <span class="gable-strip-text">
                            <span class="gable-strip-title">
                                {{ heroStripPending }} item{{ heroStripPending === 1 ? ' needs' : 's need' }} your decision
                            </span>
                            <span class="gable-strip-desc">
                                Nothing is sent, charged, or changed until you approve it.
                            </span>
                        </span>
                        <button
                            type="button"
                            class="gable-strip-btn"
                            (click)="onSelectTab('activity')">
                            Review {{ heroStripPending }}
                        </button>
                    </div>

                    <!-- Two-up card grid; only modules with pending items render.
                         Icon, tagline and tint come from GABLE_MODULE_CARDS. -->
                    <div class="gable-module-grid" *ngIf="moduleLinks.length > 0">
                        <button
                            *ngFor="let link of moduleLinks; trackBy: trackByModuleLink"
                            type="button"
                            class="gable-module-card"
                            (click)="onSelectTab('activity', link.module)">
                            <span class="gable-module-tile" [ngClass]="'tone-' + link.tone" aria-hidden="true">
                                <i class="ph-bold icon-20" [ngClass]="link.icon"></i>
                            </span>
                            <span class="gable-module-text">
                                <span class="gable-module-title">{{ link.label }}</span>
                                <span class="gable-module-tagline">{{ link.tagline }}</span>
                                <span class="gable-module-desc">{{ link.description }}</span>
                            </span>
                        </button>
                    </div>

                    <p class="gable-section-label" *ngIf="currentSuggestions.length > 0">
                        Or ask me anything
                    </p>
                </ng-container>
                <ng-template #classicGreeting>
                    <h3 class="gable-greeting">Hi, I'm Gable! How can I help…</h3>
                </ng-template>

                <!-- Page-aware suggestions only — no topic chips, no section
                     header (per Catherine 2026-07-11). -->
                <ng-container *ngIf="currentSuggestions.length > 0">
                    <ul class="gable-suggestion-list">
                        <li
                            *ngFor="let suggestion of currentSuggestions; trackBy: trackBySuggestion"
                            class="gable-suggestion-row">
                            <button
                                type="button"
                                class="gable-suggestion-btn"
                                (click)="onSuggestionClick(suggestion)">
                                <i class="ph-bold ph-arrow-elbow-down-right gable-suggestion-icon icon-muted icon-12" aria-hidden="true"></i>
                                <span class="gable-suggestion-label">{{ suggestion.label }}</span>
                            </button>
                        </li>
                    </ul>
                </ng-container>

                <!-- SD-9209 — community picker: portfolio mode only, enters drill-down scope. -->
                <div class="gable-community-picker" *ngIf="isPmcScoped && !drillDownOrg">
                    <label class="gable-section-label" for="gableCommunitySearch">Or focus on one community</label>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="gable-community-search-field">
                        <i class="ph-bold ph-magnifying-glass icon-muted icon-16" matIconPrefix aria-hidden="true"></i>
                        <input
                            matInput
                            id="gableCommunitySearch"
                            type="text"
                            placeholder="Search communities"
                            [ngModel]="communitySearch"
                            (ngModelChange)="onCommunitySearchChange($event)"
                            (focus)="onCommunitySearchFocus()">
                        <i
                            *ngIf="communityLoading"
                            matIconSuffix
                            class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin"
                            aria-hidden="true"></i>
                    </mat-form-field>
                    <ul class="gable-suggestion-list" #communityResultsList *ngIf="communityResults.length > 0">
                        <li class="gable-suggestion-row" *ngFor="let org of communityResults; trackBy: trackByOrgId">
                            <button type="button" class="gable-suggestion-btn" (click)="onPickCommunity(org)">
                                <i class="ph-bold ph-buildings gable-suggestion-icon icon-muted icon-12" aria-hidden="true"></i>
                                <span class="gable-suggestion-label">{{ org.name }}</span>
                            </button>
                        </li>
                    </ul>
                    <p
                        class="gable-community-empty"
                        *ngIf="communitySearched && !communityLoading && communityResults.length === 0">
                        No communities match your search.
                    </p>
                </div>
            </div>
        </ng-container>

        <!-- CONVERSATION STATE -->
        <ng-container *ngIf="!isEmptyState && !historyLoading">
            <div
                class="gable-messages"
                [appGableMessageHighlight]="historySearch"
                (matchesChanged)="onMatchesChanged($event)">
                <ng-container *ngFor="let msg of messages; let i = index; trackBy: trackByIndex">
                    <!-- USER -->
                    <div class="message user-message"
                         *ngIf="msg.role === 'user'"
                         [attr.data-user-msg-index]="i">
                        <span class="gable-message-attachment-chip" *ngFor="let att of msg.attachments; trackBy: trackByAttachmentFileId">
                            <i class="ph-bold ph-file-pdf icon-14" aria-hidden="true"></i>
                            <span>{{ att.fileName }}</span>
                        </span>
                        <div class="message-bubble">{{ msg.content }}</div>
                    </div>

                    <!-- ASSISTANT -->
                    <div class="message assistant-message" *ngIf="msg.role === 'assistant'">
                        <!-- Sandbox deliverable still generating (SD-7240) —
                             skeleton variant of the deliverable card previews
                             exactly what's coming. -->
                        <!-- Static label on purpose: the live progress narration
                             (ai.sandbox.progress) streams into the thought trail,
                             NOT the card's document-name row — a streaming title
                             read as wonky. The card is a calm skeleton. -->
                        <app-gable-deliverable-card
                            *ngIf="msg.sandboxState === 'generating'"
                            [loading]="true"
                            loadingLabel="Working on it…"
                            loadingHint="Usually takes a minute or two">
                        </app-gable-deliverable-card>

                        <app-alert-block
                            *ngIf="!msg.sandboxState && msg.alert"
                            [type]="msg.alert">
                            {{ msg.content }}
                        </app-alert-block>

                        <ng-container *ngIf="!msg.sandboxState && !msg.alert">
                            <ng-container
                                *ngIf="(msg.thoughts && msg.thoughts.length > 0) || !!msg.thinkingContent; else loneGable">
                                <app-ai-thought-trail
                                    class="thought-trail-block"
                                    [thoughts]="msg.thoughts || []"
                                    [thinkingContent]="msg.thinkingContent ?? null"
                                    [streaming]="msg.streamState === 'streaming'"
                                    [elapsedSeconds]="elapsedSeconds(msg)"
                                    [collapsed]="msg.thoughtsCollapsed === true"
                                    (collapsedChange)="onThoughtsCollapsedChange(msg.messageId, $event)">
                                    <!-- The Gable mascot lives ONLY in the empty-state
                                         greeting (his one "hello"); it never repeats in
                                         the message stream. -->
                                </app-ai-thought-trail>
                            </ng-container>
                            <div class="gable-long-run-notice" *ngIf="msg.longRunNotice && msg.streamState === 'streaming'">
                                <i class="ph-bold ph-sparkle icon-16" aria-hidden="true"></i>
                                <span>{{ msg.longRunNotice }}</span>
                            </div>
                            <ng-template #loneGable></ng-template>

                            <!-- gable-suggestions fenced blocks are split out of the
                                 markdown and rendered as rich decision cards below
                                 the prose (raw JSON never shows; partial blocks are
                                 hidden until the closing fence streams in). -->
                            <ng-container *ngIf="msg.content | gableSuggestions as sg">
                                <div class="bubble-row" [class.is-streaming]="msg.streamState === 'streaming'">
                                    <div
                                        class="message-bubble"
                                        [class.is-streaming]="msg.streamState === 'streaming'"
                                        [class.is-failed]="msg.streamState === 'failed'"
                                        [attr.aria-live]="msg.streamState === 'streaming' ? 'polite' : null">
                                        <span [innerHTML]="sg.text | markdown: (msg.streamState === 'streaming' ? 'streaming' : 'final') : true"
                                              [appGableDownloadLinks]="sg.text + ':' + msg.streamState"></span>
                                    </div>
                                </div>

                                <app-gable-suggestion-card
                                    *ngFor="let card of sg.cards; trackBy: trackBySuggestionCard"
                                    [payload]="card"
                                    (actionPrompt)="onSuggestionActionPrompt($event)">
                                </app-gable-suggestion-card>
                            </ng-container>

                            <!-- One "still working" heartbeat for the whole turn,
                                 pinned at the bottom. Runs while streaming until the
                                 answer starts rendering real results (a table / list) —
                                 so thought lines, preamble prose, and quiet
                                 tool-call gaps all read as one continuous live
                                 stream instead of a split thought/answer block. -->
                            <div
                                *ngIf="msg.streamState === 'streaming' && !msg.longRunNotice && !isRenderingResults(msg.content)"
                                class="gable-working-dots"
                                aria-label="Gable is working">
                                <span></span><span></span><span></span>
                            </div>

                            <div class="citations" *ngIf="msg.citations && msg.citations.length > 0">
                                <span class="citations-label">Citations</span>
                                <span
                                    *ngFor="let cite of msg.citations; trackBy: trackByCitation"
                                    class="citation-pill">
                                    {{ cite.table }}#{{ cite.id }}
                                </span>
                            </div>

                            <ng-container *ngIf="msg.toolCards && msg.toolCards.length > 0">
                                <ng-container *ngFor="let card of msg.toolCards; trackBy: trackByToolCard">
                                    <app-gable-report-card
                                        *ngIf="asReportCardPayload(card) as reportPayload"
                                        [payload]="reportPayload">
                                    </app-gable-report-card>
                                    <app-gable-budget-redirect-card
                                        *ngIf="asBudgetRedirectPayload(card) as budgetPayload"
                                        [payload]="budgetPayload">
                                    </app-gable-budget-redirect-card>
                                    <app-gable-action-preview-card
                                        *ngIf="asActionPreviewPayload(card) as previewPayload"
                                        [payload]="previewPayload"
                                        (confirm)="onActionPreviewConfirm($event)"
                                        (cancel)="onActionPreviewCancel($event)"
                                        (utilityAction)="onActionPreviewUtility($event)">
                                    </app-gable-action-preview-card>
                                    <app-gable-deliverable-card
                                        *ngIf="asDeliverableCardPayload(card) as deliverablePayload"
                                        [payload]="deliverablePayload">
                                    </app-gable-deliverable-card>
                                    <app-gable-ledger-intelligence-card
                                        *ngIf="asLedgerIntelligencePayload(card) as ledgerPayload"
                                        [payload]="ledgerPayload">
                                    </app-gable-ledger-intelligence-card>
                                </ng-container>
                            </ng-container>

            <!-- Feedback-submitted confirmation — replaces the thumbs row once
                 the user has picked a chip + Send. Locks further
                 feedback on this interaction. -->
            <app-alert-block
                *ngIf="msg.streamState === 'complete' && msg.interactionId && wasFeedbackSent(msg) && !hasPendingSandboxCard"
                type="success"
                class="feedback-submitted-alert">
                Thanks. Your feedback has been submitted to the team.
            </app-alert-block>

                            <!-- Action row. The row itself is NOT gated on
                                 `wasFeedbackSent` — copy and regenerate stay
                                 useful after feedback is submitted; only the
                                 vote controls retire (they are
                                 replaced by the green confirmation above). -->
                            <div class="msg-footer"
                                 *ngIf="msg.streamState === 'complete' && msg.interactionId && !hasPendingSandboxCard">
                                <!-- One button across all three copy states: rebuilding the
                                     node mid-interaction would throw focus away the instant
                                     the person clicked it. Only classes and content change. -->
                                <button
                                    *ngIf="canCopyMessage(msg)"
                                    type="button"
                                    class="msg-footer-btn msg-copy-btn"
                                    [class.is-copied]="copyState(msg) === 'copied'"
                                    [class.is-copy-failed]="copyState(msg) === 'failed'"
                                    matTooltip="Copy"
                                    matTooltipClass="gable-dark-tooltip"
                                    matTooltipPosition="above"
                                    [matTooltipShowDelay]="150"
                                    matTooltipTouchGestures="off"
                                    aria-label="Copy response"
                                    [copyToClipboard]="copyableText(msg)"
                                    copyConfirm="inline"
                                    (copyStateChange)="onCopyStateChange(msg, $event)">
                                    <i *ngIf="!copyState(msg)" class="ph-bold ph-copy icon-muted icon-18" aria-hidden="true"></i>
                                    <i *ngIf="copyState(msg) === 'copied'" class="ph-bold ph-check icon-success icon-18" aria-hidden="true"></i>
                                    <i *ngIf="copyState(msg) === 'failed'" class="ph-bold ph-warning-circle icon-error icon-18" aria-hidden="true"></i>
                                    <!-- Always rendered so the live region exists before its
                                         text changes — a region created at the same moment as
                                         its content is not announced. -->
                                    <span class="msg-footer-btn-label" aria-live="polite">
                                        <ng-container *ngIf="copyState(msg) === 'copied'">Copied</ng-container>
                                        <ng-container *ngIf="copyState(msg) === 'failed'">Couldn't copy</ng-container>
                                    </span>
                                </button>
                                <button
                                    *ngIf="!wasFeedbackSent(msg)"
                                    type="button"
                                    class="msg-footer-btn msg-vote-btn msg-vote-up"
                                    [class.active]="getFeedback(msg.messageId) === 'good'"
                                    [attr.aria-pressed]="getFeedback(msg.messageId) === 'good'"
                                    (click)="onFeedback(msg, 'good')"
                                    matTooltip="Good answer"
                                    matTooltipClass="gable-dark-tooltip"
                                    matTooltipPosition="above"
                                    [matTooltipShowDelay]="150"
                                    matTooltipTouchGestures="off"
                                    aria-label="Good answer">
                                    <i class="ph-bold ph-thumbs-up icon-muted icon-18" aria-hidden="true"></i>
                                </button>
                                <button
                                    *ngIf="!wasFeedbackSent(msg)"
                                    type="button"
                                    class="msg-footer-btn msg-vote-btn msg-vote-down"
                                    [class.active]="getFeedback(msg.messageId) === 'bad'"
                                    [attr.aria-pressed]="getFeedback(msg.messageId) === 'bad'"
                                    (click)="onFeedback(msg, 'bad')"
                                    matTooltip="Report a problem"
                                    matTooltipClass="gable-dark-tooltip"
                                    matTooltipPosition="above"
                                    [matTooltipShowDelay]="150"
                                    matTooltipTouchGestures="off"
                                    aria-label="Report a problem">
                                    <i class="ph-bold ph-thumbs-down icon-muted icon-18" aria-hidden="true"></i>
                                </button>
                            </div>

                            <!-- Inline reason-chips feedback panel — slides in below the thumbs row. -->
                            <app-gable-feedback-panel
                                *ngIf="msg.interactionId !== undefined && !wasFeedbackSent(msg) && !hasPendingSandboxCard"
                                [open]="isFeedbackPanelOpenFor(msg.messageId)"
                                (chipSelected)="onChipFeedback(msg, $event)"
                                (skipped)="closeFeedbackPanel()">
                            </app-gable-feedback-panel>

                            <!-- Shares the new-chat suggestion chip markup so the two stay identical. -->
                            <ul class="gable-suggestion-list gable-followup-strip"
                                *ngIf="isLastAssistantMessage(i) && apiSuggestions.length > 0">
                                <li
                                    *ngFor="let suggestion of apiSuggestions; trackBy: trackByApiSuggestion"
                                    class="gable-suggestion-row">
                                    <button
                                        type="button"
                                        class="gable-suggestion-btn"
                                        [disabled]="isSending"
                                        (click)="onApiSuggestionClick(suggestion)">
                                        <i class="ph-bold ph-arrow-elbow-down-right gable-suggestion-icon icon-muted icon-12" aria-hidden="true"></i>
                                        <span class="gable-suggestion-label">{{ suggestion }}</span>
                                    </button>
                                </li>
                            </ul>
                        </ng-container>
                    </div>
                </ng-container>

                <!-- Pre-stream thinking placeholder -->
                <div
                    class="message assistant-message"
                    *ngIf="isSending && !hasStreamingAssistant && (!pendingQuestions || submittingAnswers)">
                    <div class="thinking-bubble">
                        <!-- No mascot in the stream — it lives only in the
                             empty-state greeting. -->
                        <span>Thinking…</span>
                    </div>
                </div>

                <!-- Question card -->
                <app-question-card
                    *ngIf="pendingQuestions && !submittingAnswers"
                    [batch]="pendingQuestions"
                    [submitting]="submittingAnswers"
                    (submitAnswers)="onAnswers($event)"
                    (close)="onCloseQuestionCard()">
                </app-question-card>
            </div>

        </ng-container>
    </section>

    <!-- ============================================================ -->
    <!-- Activity — the review queue + feed (mock preview; SD-7969     -->
    <!-- wires it to pending proposals + the commit log).              -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel"
        cdkScrollable
        *ngIf="autopilotUi && activeTab === 'activity'">
        <app-gable-activity-panel [initialFilter]="activityFilter"></app-gable-activity-panel>
    </section>

    <!-- ============================================================ -->
    <!-- Skills — global library management (SD-9197, super-only).    -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel"
        cdkScrollable
        *ngIf="activeTab === 'skills' && isSuperUser">
        <app-gable-skills-panel (switchToChat)="onSelectTab('chat')"></app-gable-skills-panel>
    </section>

    <!-- ============================================================ -->
    <!-- Preferences — standing "always…" / "never…" instructions      -->
    <!-- (SD-9309). Visible to every Gable user, not super-gated.       -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel gable-prefs-panel"
        cdkScrollable
        *ngIf="activeTab === 'prefs'">
        <!-- One block, not a splash header plus a second "nothing here" line —
             the same merge already made on Activity and Skills. -->
        <div class="gable-prefs-empty" *ngIf="prefsPanelEmpty && !prefsComposerOpen">
            <span class="gable-prefs-empty-icon" aria-hidden="true">
                <i class="ph-bold ph-list-checks icon-20" aria-hidden="true"></i>
            </span>
            <h3 class="gable-prefs-empty-title">No instructions yet</h3>
            <p class="gable-prefs-empty-copy">
                An instruction is a standing rule Gable follows in every conversation, without being
                asked. Add one here, or tell Gable to "always…" or "never…" in chat. They apply to
                your chats only.
            </p>
            <!-- The whole block stands down while the composer is open: the
                 composer REPLACES it rather than opening above it, because the
                 list that hosts the composer and this block are mutually
                 exclusive on `prefsPanelEmpty`. Leaving the CTA up would be a
                 dead control, and leaving the examples up would be three. -->
            <!-- Carries the same template ref as the ghost trigger: only ever one
                 of the two is mounted, so closing the composer can hand focus back
                 to whichever one the user opened it from. -->
            <button
                #prefsAddTrigger
                type="button"
                class="gable-prefs-add-btn gable-prefs-empty-btn"
                *ngIf="!prefsComposerOpen"
                (click)="onOpenPrefsComposer()">
                <i class="ph-bold ph-plus icon-16" aria-hidden="true"></i>
                Add an instruction
            </button>
            <div class="gable-prefs-examples" *ngIf="!prefsComposerOpen">
                <span class="gable-prefs-examples-label">Try one of these</span>
                <button
                    type="button"
                    class="gable-prefs-example"
                    *ngFor="let example of prefsExamples; trackBy: trackByExamplePreference"
                    (click)="onUseExamplePreference(example)">
                    {{ example }}
                </button>
            </div>
        </div>

        <!-- Same toolbar shape as the archived panel, gated like the Skills one:
             search over an empty list is a control with nothing to do. -->
        <div class="gable-prefs-toolbar" *ngIf="!prefsPanelEmpty && !preferencesLoading && !preferencesLoadFailed">
            <div class="gable-archived-search gable-prefs-search">
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="gable-archived-search-field">
                    <i class="ph-bold ph-magnifying-glass icon-muted icon-16" matIconPrefix aria-hidden="true"></i>
                    <input
                        #prefsSearchInput
                        matInput
                        type="text"
                        placeholder="Search instructions"
                        aria-label="Search instructions"
                        [ngModel]="prefsSearch"
                        (ngModelChange)="onPrefsSearchChange($event)"
                        (keydown.escape)="$event.preventDefault(); onClearPrefsSearch()">
                    <span class="gable-find-controls" matIconSuffix *ngIf="prefsSearch">
                        <button
                            type="button"
                            class="gable-find-btn"
                            matTooltip="Clear search (Esc)"
                            matTooltipClass="gable-dark-tooltip"
                            (click)="onClearPrefsSearch()"
                            aria-label="Clear search">
                            <i class="ph-bold ph-x icon-muted icon-14" aria-hidden="true"></i>
                        </button>
                    </span>
                </mat-form-field>
            </div>

            <!-- Only once both sources are represented — see prefsSourceOptions. -->
            <button
                *ngIf="prefsSourceOptions.length > 0"
                type="button"
                class="gable-archived-filter"
                [class.is-set]="prefsSourceFilter !== ''"
                [matMenuTriggerFor]="prefsSourceMenu">
                <span class="gable-archived-filter-label">Filter by</span>
                <span class="gable-archived-filter-value">{{ prefsSourceFilterLabel }}</span>
                <i class="ph-bold ph-caret-down icon-muted icon-12" aria-hidden="true"></i>
            </button>
            <mat-menu #prefsSourceMenu="matMenu" class="gable-menu-panel">
                <button
                    mat-menu-item
                    [attr.aria-current]="prefsSourceFilter === '' ? 'true' : null"
                    (click)="onPrefsSourceFilterChange(null)">
                    <span>All</span>
                </button>
                <button
                    mat-menu-item
                    *ngFor="let opt of prefsSourceOptions; trackBy: trackByScopeOption"
                    [attr.aria-current]="prefsSourceFilter === opt.key ? 'true' : null"
                    (click)="onPrefsSourceFilterChange(opt.key)">
                    <span>{{ opt.label }}</span>
                </button>
            </mat-menu>
        </div>

        <!-- Rows in outline, not a spinner: the panel opens straight onto a
             list, so showing its shape is both faster-feeling and honest about
             what is coming. `preferencesLoading` is safe to gate on — it is set
             by tab activation and retry only, never by a filter change (those
             re-slice in memory) or by a row save. -->
        <div class="gable-prefs-skeleton" *ngIf="preferencesLoading" role="status" aria-live="polite" aria-busy="true">
            <span class="visually-hidden">Loading instructions</span>
            <div class="gable-prefs-skeleton-row" *ngFor="let row of skeletonRows3; trackBy: trackByIndex" aria-hidden="true">
                <span class="gable-skeleton-line gable-skeleton-line--lg"></span>
                <span class="gable-skeleton-line gable-skeleton-line--sm"></span>
            </div>
        </div>

        <!-- Names the list and rules it off. Without it the rows floated under
             the toolbar with nothing marking where the list began — and a
             single-row list showed no separator at all. -->
        <div class="gable-prefs-list-header" *ngIf="!preferencesLoading && !preferencesLoadFailed && filteredPreferences.length > 0">
            {{ prefsCountLabel }}
        </div>

        <!-- `|| prefsComposerOpen` is load-bearing, not defensive: the composer's
             only mount site is the <li> below, and without this term the <ul> and
             the empty state were strict complements — so on a panel with zero
             instructions the empty state's CTA opened a composer that had nowhere
             to render, and deleting the last row while composing destroyed the
             draft. The empty state stands down in exchange, so at most one
             #prefsAddTrigger is mounted in every state. -->
        <ul class="gable-prefs-list" *ngIf="!preferencesLoading && !preferencesLoadFailed && (!prefsPanelEmpty || prefsComposerOpen)">
            <!-- Adding is one control deep, not a box standing open above the list:
                 the panel opens on the instructions the user came to read, and the
                 composer takes its space only once it is asked for. It replaces its
                 own trigger in place, so opening it moves the rows below by the
                 box's height and nothing else. -->
            <!-- The house textarea: `matInput` in an outlined `mat-form-field`, the
                 same control the golden reference specifies and the same chrome as
                 the search field above it. Starts at ONE row and grows to four in
                 the component: at two rows an empty box shows a blank line under
                 the placeholder, which reads as a rendering fault rather than as
                 room to type. See `_autosize` for why not `cdkTextareaAutosize`. -->
            <li class="gable-prefs-add" *ngIf="prefsComposerOpen">
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="gable-prefs-add-field">
                    <textarea
                        #prefsAddInput
                        matInput
                        class="gable-prefs-add-input"
                        rows="1"
                        maxlength="500"
                        placeholder="Add an instruction, e.g. always CC the board on violations"
                        aria-label="Add an instruction"
                        [ngModel]="newPreferenceText"
                        (ngModelChange)="onNewPreferenceChange($event)"
                        (keydown)="onPrefsKeydown($event)"></textarea>
                </mat-form-field>
                <div class="gable-prefs-add-footer">
                    <!-- Not itself a live region: bound to the visible count it
                         re-announced on every keystroke past 400 characters. The
                         announcement is a separate, always-mounted region that
                         speaks at milestones only — a live region also has to
                         exist before its content changes to be announced at all,
                         which an *ngIf'd one does not. -->
                    <span class="gable-prefs-counter">
                        {{ prefsCharsLeft !== null ? prefsCharsLeft + ' characters left' : '' }}
                    </span>
                    <span class="visually-hidden" aria-live="polite">
                        {{ prefsCharsLeftAnnouncement(newPreferenceText) }}
                    </span>
                    <button type="button" class="gable-prefs-cancel-btn" (click)="onClosePrefsComposer()">
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="gable-prefs-add-btn"
                        [disabled]="!newPreferenceText.trim() || savingPreference"
                        (click)="onAddPreference()">
                        Save
                    </button>
                </div>
            </li>

            <!-- The first row of the list, under its count header: adding an
                 instruction puts one HERE, so the control sits where the result
                 will. Its own row rather than a third control inside the toolbar —
                 the docked drawer is 320px wide, where search + filter + a button
                 wraps to two lines. A real list item, so the list's own rhythm
                 spaces it: the panel is a 24px-gap flex column, and an add slot
                 living in THAT stack could only fake row spacing with negative
                 margins — which is exactly how it started overlapping the rows.
                 The list is why its own *ngIf no longer repeats the load guards:
                 the <ul> now renders whenever instructions exist, INCLUDING when
                 a search has filtered every row out of view, where adding must
                 still be possible. The empty state carries its own filled CTA
                 instead; a quiet row under a centered splash reads as
                 decoration. -->
            <li *ngIf="!prefsComposerOpen">
                <button
                    #prefsAddTrigger
                    type="button"
                    class="gable-prefs-add-trigger"
                    (click)="onOpenPrefsComposer()">
                    <i class="ph-bold ph-plus icon-16" aria-hidden="true"></i>
                    <span>Add an instruction</span>
                </button>
            </li>
            <li
                class="gable-prefs-row"
                [class.is-editing]="editingPreferenceId === pref.id"
                *ngFor="let pref of filteredPreferences; trackBy: trackByPreferenceId">
                <!-- Edit swaps the row's contents rather than opening a dialog:
                     the instruction stays in the list it belongs to, and the
                     rows around it don't move. -->
                <ng-container *ngIf="editingPreferenceId === pref.id; else readRow">
                    <mat-form-field
                        appearance="outline"
                        subscriptSizing="dynamic"
                        class="gable-prefs-edit-field">
                        <textarea
                            #prefsEditInput
                            matInput
                            class="gable-prefs-add-input"
                            rows="1"
                            maxlength="500"
                            aria-label="Edit instruction"
                            [ngModel]="editingPreferenceText"
                            (ngModelChange)="onEditPreferenceTextChange($event)"
                            (keydown)="onPrefsEditKeydown($event, pref)"></textarea>
                    </mat-form-field>
                    <div class="gable-prefs-edit-actions">
                        <!-- Same limit as the composer, so the same warning: editing
                             a 470-character instruction otherwise stopped dead at
                             500 with nothing on screen saying why. -->
                        <span class="gable-prefs-counter">
                            {{ prefsEditCharsLeft !== null ? prefsEditCharsLeft + ' characters left' : '' }}
                        </span>
                        <span class="visually-hidden" aria-live="polite">
                            {{ prefsCharsLeftAnnouncement(editingPreferenceText) }}
                        </span>
                        <button
                            type="button"
                            class="gable-prefs-cancel-btn"
                            (click)="onCancelEditPreference()">
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="gable-prefs-add-btn"
                            [disabled]="!editingPreferenceText.trim() || savingPreferenceEdit"
                            (click)="onSavePreferenceEdit(pref)">
                            Save
                        </button>
                    </div>
                </ng-container>

                <ng-template #readRow>
                    <span class="gable-prefs-body" [innerHTML]="pref.body | highlight: prefsSearch"></span>
                    <!-- Source and date share the archived panel's right-hand slot,
                         and the actions are absolutely positioned over it: the swap
                         on hover costs no layout shift, so the row can't reflow
                         under the cursor. focus-within keeps them reachable by
                         keyboard. -->
                    <span class="gable-prefs-meta">{{ preferenceMetaLabel(pref) }}</span>
                    <span class="gable-prefs-actions">
                        <button
                            type="button"
                            class="gable-prefs-row-btn"
                            matTooltip="Edit instruction"
                            matTooltipClass="gable-dark-tooltip"
                            aria-label="Edit instruction"
                            (click)="onEditPreference(pref, $event)">
                            <i class="ph-bold ph-pencil-simple icon-16" aria-hidden="true"></i>
                        </button>
                        <button
                            type="button"
                            class="gable-prefs-row-btn gable-prefs-delete-btn"
                            matTooltip="Delete instruction"
                            matTooltipClass="gable-dark-tooltip"
                            aria-label="Delete instruction"
                            (click)="onDeletePreference(pref)">
                            <i class="ph-bold ph-trash icon-16" aria-hidden="true"></i>
                        </button>
                    </span>
                </ng-template>
            </li>
        </ul>

        <!-- Distinct from the empty state above: the list has rows, the filters
             just aren't matching any of them. -->
        <p
            class="gable-prefs-no-match"
            role="status"
            *ngIf="!prefsPanelEmpty && !preferencesLoading && !preferencesLoadFailed && filteredPreferences.length === 0">
            {{ prefsEmptyMessage }}
        </p>

        <p class="gable-prefs-error" *ngIf="preferencesLoadFailed && !preferencesLoading">
            Couldn't load your instructions.
            <button type="button" class="gable-prefs-retry" (click)="retryLoadPreferences()">Try again</button>
        </p>
    </section>

    <!-- ============================================================ -->
    <!-- Archived chats — full panel (search / filter / sort), not an  -->
    <!-- inline rail section. Renders in BOTH modes: the `*ngIf` below -->
    <!-- carries no mode test, and the drawer menu's Archived item     -->
    <!-- opens this panel. It was expanded-only until f390bf586, which -->
    <!-- collapsed the docked drawer's select-only archived submenu    -->
    <!-- into this one panel so a chat can still be restored below     -->
    <!-- 960px.                                                        -->
    <!-- SD-9563 F-b98e: this comment used to claim the opposite.      -->

    <!-- ============================================================ -->
    <!-- ============================================================ -->
    <section
        class="gable-body gable-body-panel gable-archived-panel"
        *ngIf="isOpen && activeTab === 'archived'">
        <div class="gable-panel-header" *ngIf="archivedPanelEmpty">
            <span class="gable-panel-header-icon" aria-hidden="true">
                <i class="ph-bold ph-archive icon-20" aria-hidden="true"></i>
            </span>
            <h3 class="gable-panel-header-title">Archived chats</h3>
            <p class="gable-panel-header-subtitle">Chats you've moved out of the way. Restore one to bring it back to recents.</p>
        </div>

        <div class="gable-archived-toolbar">
            <!-- Same field as the rail's search box: the shared
                 `.gable-sidebar-search, .gable-archived-search` block owns the
                 appearance so the two can't drift apart. -->
            <div class="gable-archived-search">
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="gable-archived-search-field">
                    <i class="ph-bold ph-magnifying-glass icon-muted icon-16" matIconPrefix aria-hidden="true"></i>
                    <input
                        #archivedSearchInput
                        matInput
                        type="text"
                        placeholder="Search archived chats"
                        aria-label="Search archived chats"
                        [ngModel]="archivedSearch"
                        (ngModelChange)="onArchivedSearchChange($event)"
                        (keydown.escape)="$event.preventDefault(); onClearArchivedSearch()">
                    <!-- Same negative trim as the rail's find controls: the
                         suffix container's 14px right padding would otherwise
                         push the X past the magnifier's inset. -->
                    <span class="gable-find-controls" matIconSuffix *ngIf="archivedSearch">
                        <button
                            type="button"
                            class="gable-find-btn"
                            matTooltip="Clear search (Esc)"
                            matTooltipClass="gable-dark-tooltip"
                            (click)="onClearArchivedSearch()"
                            aria-label="Clear search">
                            <i class="ph-bold ph-x icon-muted icon-14" aria-hidden="true"></i>
                        </button>
                    </span>
                    <i
                        *ngIf="archivedLoading && !archivedSearch"
                        matIconSuffix
                        class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin"
                        aria-hidden="true"></i>
                </mat-form-field>
            </div>

            <!-- Scope filter appears only when the archive actually spans more
                 than one tenant — a one-choice filter is noise. -->
            <button
                *ngIf="archivedScopeOptions.length > 0"
                type="button"
                class="gable-archived-filter"
                [class.is-set]="archivedScopeFilter !== ''"
                [matMenuTriggerFor]="archivedScopeMenu">
                <span class="gable-archived-filter-label">Filter by</span>
                <span class="gable-archived-filter-value">{{ archivedScopeFilterLabel }}</span>
                <i class="ph-bold ph-caret-down icon-muted icon-12" aria-hidden="true"></i>
            </button>
            <mat-menu #archivedScopeMenu="matMenu" class="gable-menu-panel">
                <button
                    mat-menu-item
                    [attr.aria-current]="archivedScopeFilter === '' ? 'true' : null"
                    (click)="onArchivedScopeFilterChange(null)">
                    <span>All</span>
                </button>
                <button
                    mat-menu-item
                    *ngFor="let opt of archivedScopeOptions; trackBy: trackByScopeOption"
                    [attr.aria-current]="archivedScopeFilter === opt.key ? 'true' : null"
                    (click)="onArchivedScopeFilterChange(opt)">
                    <span>{{ opt.label }}</span>
                </button>
            </mat-menu>

            <button
                type="button"
                class="gable-archived-filter"
                [matMenuTriggerFor]="archivedSortMenu">
                <i class="ph-bold ph-arrows-down-up icon-muted icon-14" aria-hidden="true"></i>
                <span class="gable-archived-filter-value">{{ archivedSortLabel }}</span>
                <i class="ph-bold ph-caret-down icon-muted icon-12" aria-hidden="true"></i>
            </button>
            <mat-menu #archivedSortMenu="matMenu" class="gable-menu-panel">
                <button mat-menu-item [attr.aria-current]="archivedSort === 'recent' ? 'true' : null" (click)="onArchivedSortChange('recent')"><span>Recent activity</span></button>
                <button mat-menu-item [attr.aria-current]="archivedSort === 'oldest' ? 'true' : null" (click)="onArchivedSortChange('oldest')"><span>Oldest first</span></button>
                <button mat-menu-item [attr.aria-current]="archivedSort === 'title' ? 'true' : null" (click)="onArchivedSortChange('title')"><span>Name (A–Z)</span></button>
            </mat-menu>
        </div>

        <!-- Windowed: an archive grows by a row a day and the panel appends
             25-row pages forever, so the DOM is capped at what fits plus a
             buffer. The viewport owns the scrolling the section used to do,
             which is also what drives paging (onArchivedScrolled). -->
        <cdk-virtual-scroll-viewport
            #archivedViewport
            class="gable-archived-viewport"
            [itemSize]="archivedRowHeightPx"
            minBufferPx="200"
            maxBufferPx="400"
            (scrolledIndexChange)="onArchivedScrolled()">
        <ul class="gable-archived-list" *ngIf="archivedRows.length > 0">
            <li
                class="gable-archived-row"
                *cdkVirtualFor="let c of archivedRows; trackBy: trackByConversationId; let last = last"
                [class.is-last]="last"
                [class.is-editing]="editingConversationId === c.id">
                <input
                    *ngIf="editingConversationId === c.id"
                    class="gable-sidebar-rename-input"
                    type="text"
                    maxlength="255"
                    [(ngModel)]="editingTitle"
                    (keydown.enter)="$event.preventDefault(); commitInlineRename(c)"
                    (keydown.escape)="$event.preventDefault(); cancelInlineRename()"
                    (blur)="commitInlineRename(c)"
                    (click)="$event.stopPropagation()"
                    aria-label="Rename chat">
                <ng-container *ngIf="editingConversationId !== c.id">
                    <!-- Opening an archived chat does NOT restore it: reading is
                         not un-archiving. Restore is the explicit row action. -->
                    <button type="button" class="gable-archived-open" (click)="onSelectConversation(c)">
                        <span class="gable-archived-text">
                            <span class="gable-archived-title" [innerHTML]="conversationLabel(c) | highlight: archivedSearch"></span>
                            <!-- Only cross-tenant chats carry this — the scope is the
                                 one thing the title can't tell you. Inline rather than
                                 a second line so every row is the same height, which
                                 is what the fixed-size viewport above requires. -->
                            <span class="gable-archived-meta" *ngIf="archivedScopeLabel(c) as scopeLabel">{{ scopeLabel }}</span>
                        </span>
                        <span class="gable-archived-date">{{ archivedDateLabel(c) }}</span>
                    </button>
                    <button
                        type="button"
                        class="gable-archived-overflow"
                        [matMenuTriggerFor]="archivedPanelRowMenu"
                        matTooltip="Chat options"
                        matTooltipClass="gable-dark-tooltip"
                        aria-label="Chat options"
                        (click)="$event.stopPropagation()">
                        <i class="ph-bold ph-dots-three icon-muted icon-16" aria-hidden="true"></i>
                    </button>
                    <mat-menu #archivedPanelRowMenu="matMenu" xPosition="before" class="gable-menu-panel">
                        <button mat-menu-item (click)="onUnarchiveConversation(c)">
                            <i class="ph-bold ph-arrow-counter-clockwise icon-muted icon-16" aria-hidden="true"></i>
                            <span>Restore</span>
                        </button>
                        <button mat-menu-item (click)="onRenameConversation(c)">
                            <i class="ph-bold ph-pencil-simple icon-muted icon-16" aria-hidden="true"></i>
                            <span>Rename</span>
                        </button>
                    </mat-menu>
                </ng-container>
            </li>
        </ul>
        </cdk-virtual-scroll-viewport>

        <!-- Suppress the empty copy while page 1 is still in flight so it can't
             flash before the rows land. -->
        <p
            class="gable-archived-empty"
            role="status"
            *ngIf="archivedRows.length === 0 && !archivedLoading">
            {{ archivedEmptyMessage }}
        </p>

        <div class="gable-sidebar-loading" *ngIf="archivedLoading" aria-busy="true">
            <i class="ph-bold ph-circle-notch icon-muted icon-16 gable-spin" aria-hidden="true"></i>
            <span>Loading…</span>
        </div>
    </section>

    <!-- SD-9246 — calm notice for the frozen 409 turn_in_flight contract; not the red error state. -->
    <div class="gable-turn-in-flight-notice" *ngIf="turnInFlightNotice && activeTab === 'chat'">
        <i class="ph-bold ph-hourglass-medium icon-16" aria-hidden="true"></i>
        <span>Gable is still working on your previous request&hellip;</span>
        <button type="button"
                class="gable-ask-new-chat-btn"
                *ngIf="draftMessage.trim()"
                (click)="askInNewChat()">
            Ask in a new chat
        </button>
    </div>

    <!-- ============================================================ -->
    <!-- FOOTER / COMPOSER                                            -->
    <!-- ============================================================ -->
    <footer class="gable-footer" *ngIf="!pendingQuestions && activeTab === 'chat'">
        <div class="gable-composer">
            <div class="gable-mic-notice" role="alert" *ngIf="dictation.error">
                <i class="ph-bold ph-microphone-slash icon-16" aria-hidden="true"></i>
                <span class="gable-mic-notice-text">{{ dictation.error }}</span>
                <button
                    type="button"
                    class="gable-mic-notice-dismiss"
                    aria-label="Dismiss"
                    (click)="onDismissDictationError()">
                    <i class="ph-bold ph-x icon-12" aria-hidden="true"></i>
                </button>
            </div>
            <div class="gable-attachment-chip-row" *ngIf="pendingAttachment || attachmentUploading">
                <span class="gable-attachment-chip">
                    <i
                        class="ph-bold icon-16"
                        [class.ph-file-pdf]="!attachmentUploading"
                        [class.ph-circle-notch]="attachmentUploading"
                        [class.gable-spin]="attachmentUploading"
                        aria-hidden="true"></i>
                    <span class="gable-attachment-chip-label">{{ attachmentUploading ? 'Uploading…' : pendingAttachment?.fileName }}</span>
                    <button
                        *ngIf="pendingAttachment && !attachmentUploading"
                        type="button"
                        class="gable-attachment-chip-remove"
                        aria-label="Remove attachment"
                        (click)="onRemovePendingAttachment()">
                        <i class="ph-bold ph-x icon-12" aria-hidden="true"></i>
                    </button>
                </span>
            </div>
            <textarea
                #messageInput
                class="gable-composer-input"
                rows="1"
                [maxlength]="composerMaxLength"
                [placeholder]="composerPlaceholder"
                [ngModel]="composerValue"
                (ngModelChange)="onDraftChange($event)"
                (keydown)="onKeydown($event)"
                [disabled]="isSending && !backgroundMode"></textarea>
            <span class="gable-composer-count" *ngIf="composerValue.length >= 9000">{{ composerValue.length | number }} / 10,000</span>
            <div class="gable-composer-actions">
                <input
                    #attachmentFileInput
                    type="file"
                    class="gable-attachment-input-hidden"
                    accept="application/pdf"
                    (change)="onAttachmentFileSelected($event)">

                <!-- While dictating, the whole row becomes the recording
                     control: a live level meter plus the cancel/confirm pair.
                     Cancel is the only destructive way out, so it is never
                     the same button as stop. -->
                <ng-container *ngIf="dictation.listening; else composerRestingActions">
                    <div
                        #waveformBarsEl
                        class="gable-mic-waveform"
                        role="img"
                        aria-label="Microphone input level">
                        <span class="gable-mic-bar" *ngFor="let bar of waveformBars; trackBy: trackByIndex"></span>
                    </div>
                    <button
                        type="button"
                        class="gable-mic-cancel-btn"
                        (click)="onCancelDictation()"
                        matTooltip="Discard what you said"
                        matTooltipClass="gable-dark-tooltip"
                        aria-label="Cancel dictation">
                        <i class="ph-bold ph-x icon-16" aria-hidden="true"></i>
                    </button>
                    <button
                        type="button"
                        class="gable-send-btn"
                        (click)="onConfirmDictation()"
                        matTooltip="Keep the transcript"
                        matTooltipClass="gable-dark-tooltip"
                        aria-label="Use transcript">
                        <i class="ph-bold ph-check icon-16" aria-hidden="true"></i>
                    </button>
                </ng-container>

                <ng-template #composerRestingActions>
                    <button
                        type="button"
                        class="gable-attach-btn"
                        [disabled]="attachmentUploading || !!pendingAttachment"
                        (click)="onAttachClick()"
                        matTooltip="Upload a PDF for Gable to analyze"
                        matTooltipClass="gable-dark-tooltip"
                        aria-label="Attach a PDF">
                        <i
                            class="ph-bold icon-16"
                            [class.ph-paperclip]="!attachmentUploading"
                            [class.ph-circle-notch]="attachmentUploading"
                            [class.gable-spin]="attachmentUploading"
                            aria-hidden="true"></i>
                    </button>
                    <flex-spacer></flex-spacer>
                    <!-- No SpeechRecognition (Firefox) means no mic at all: a
                         mic that cannot listen is a broken control, not a
                         disabled one. -->
                    <button
                        *ngIf="dictation.supported && (!isSending || backgroundMode)"
                        type="button"
                        class="gable-mic-btn"
                        (click)="onToggleDictation()"
                        matTooltip="Dictate your question"
                        matTooltipClass="gable-dark-tooltip"
                        aria-label="Dictate your question">
                        <i class="ph-bold ph-microphone icon-16" aria-hidden="true"></i>
                    </button>
                    <!-- Submit toggles into a Stop button while a stream is
                         in flight, mirroring Claude desktop. Clicking it
                         cancels the in-flight assistant turn. -->
                    <button
                        *ngIf="!isSending || backgroundMode"
                        type="button"
                        class="gable-send-btn"
                        [disabled]="!composerValue.trim()"
                        (click)="onSend()"
                        aria-label="Send message">
                        <i class="ph-bold ph-arrow-up icon-16" aria-hidden="true"></i>
                    </button>
                    <button
                        *ngIf="isSending && !backgroundMode"
                        type="button"
                        class="gable-send-btn"
                        (click)="onStopStream()"
                        aria-label="Stop response">
                        <i class="ph-bold ph-stop-circle icon-16" aria-hidden="true"></i>
                    </button>
                </ng-template>
            </div>
        </div>
        <p class="gable-composer-hint">Gable can make mistakes — double-check important info.</p>
    </footer>

    </div><!-- /.gable-conversation-col -->
    </div><!-- /.gable-conversation -->

    </div><!-- /.gable-main -->
    </div><!-- /.gable-content-row -->
</div>
